/**
 * Stored in `policies.effect` when creating rows from the Set Permissions form.
 * Match your backend `rolePermissionPolicyRowSchema.effect`.
 */
export enum PolicyEffect {
  ALLOW = "ALLOW",
  DENY = "DENY",
}

/**
 * Policy codes stored in `policies.condition_key` (same as DB catalog `policy_condition_keys.code`).
 * RBAC answers “can they use this permission?”; these codes answer “which rows?” when combined
 * as rows under `Permission → Policy`.
 */
export enum PolicyConditionKey {
  ALL = "ALL",
  CREATED_BY_SELF = "CREATED_BY_SELF",
  OWNED_BY_SELF = "OWNED_BY_SELF",
  ASSIGNED_TO_SELF = "ASSIGNED_TO_SELF",
}

/**
 * Developer-readable aliases -> same string values as {@link PolicyConditionKey}.
 * Prefer attaching real `Policy` rows in DB; use this enum only in code for readability.
 */
export enum ScopeHint {
  ALL = PolicyConditionKey.ALL,
  CREATED = PolicyConditionKey.CREATED_BY_SELF,
  OWNED = PolicyConditionKey.OWNED_BY_SELF,
  ASSIGNED = PolicyConditionKey.ASSIGNED_TO_SELF,
}

/** UI labels for policy keys (Set Permissions form). */
export const POLICY_CONDITION_KEY_LABELS: Record<PolicyConditionKey, string> = {
  [PolicyConditionKey.ALL]: "All records",
  [PolicyConditionKey.CREATED_BY_SELF]: "Created by me",
  [PolicyConditionKey.OWNED_BY_SELF]: "Owned by me",
  [PolicyConditionKey.ASSIGNED_TO_SELF]: "Assigned to me",
};

/** Keys shown in the role permission form (subset is fine; extend when backend seeds more). */
export const POLICY_KEYS_FOR_ROLE_FORM: PolicyConditionKey[] = [
  PolicyConditionKey.ALL,
  PolicyConditionKey.CREATED_BY_SELF,
  PolicyConditionKey.OWNED_BY_SELF,
  PolicyConditionKey.ASSIGNED_TO_SELF,
];

export function policyCodeGrantsUnrestrictedAccess(code: string): boolean {
  return code === PolicyConditionKey.ALL;
}

export const POLICY_CONDITION_KEY_SEED: Array<{
  code: PolicyConditionKey;
  name: string;
  sortOrder: number;
}> = [
  { code: PolicyConditionKey.ALL, name: "All records", sortOrder: 10 },
  {
    code: PolicyConditionKey.CREATED_BY_SELF,
    name: "Created by me",
    sortOrder: 20,
  },
  {
    code: PolicyConditionKey.OWNED_BY_SELF,
    name: "Owned by me",
    sortOrder: 30,
  },
  {
    code: PolicyConditionKey.ASSIGNED_TO_SELF,
    name: "Assigned to me",
    sortOrder: 40,
  },
];
